import { Palette, PaletteMode, styled, ThemeOptions } from "@mui/material";
import { customPalette } from "./palette";
import { breakpointValues, customBreakpoints } from "./breakpoints";
import { pxToRem } from "../utils/fontSizeUtility";
import { customTypographyOptions, standardTypographyOptions } from "./typography";
import { themeStandard } from "./paletteStandard";
import { TypographyOptions } from "@mui/material/styles/createTypography";

declare module "@mui/material/Paper" {
  // noinspection JSUnusedGlobalSymbols
  interface PaperPropsVariantOverrides {
    Assistant: true;
    ChatBlockTimestamp: true;
    ChatBubbleContent: true;
    DetailsBar: true;
    DetailsTabBar: true;
    DetailsTabPanel: true;
    LoanDetails: true;
    PageHeader: true;
    Highlights: true;
    UserChatBlockTimestamp: true;
    WorkItem: true;
    AccountSettings: true;
    ErrorDialogTop: true;
    ErrorDialogBottom: true;
    UserMenu: true;
    UserMenuLeftBar: true;
    TabPanel: true;
  }
}

declare module "@mui/material/Button" {
  // noinspection JSUnusedGlobalSymbols
  interface ButtonPropsVariantOverrides {
    ErrorDialog: true;
  }
}

declare module "@mui/material/styles" {
  // noinspection JSUnusedGlobalSymbols
  interface Components {
    [key: string]: any;
  }
}

interface ChatBubbleDivProps {
  variant?: "outbound" | "info" | "warn" | "critical" | "success";
}

export const ChatBubbleDiv = styled("div", {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "sx",
  name: "ChatBubbleDiv",
  slot: "Root",
  overridesResolver: (_props, styles) => [styles.root],
})<ChatBubbleDivProps>({});

interface PageHeaderCalloutDivProps {
  variant?: "top" | "right" | "bottom" | "left";
}

export const PageHeaderCalloutDiv = styled("div", {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "sx",
  name: "PageHeaderCalloutDiv",
  slot: "Root",
  overridesResolver: (_props, styles) => [styles.root],
})<PageHeaderCalloutDivProps>({});

export const appHeaderHeight = 64;
export const pageHeaderHeight = 80;
export const tabPanelTopPadding = 16;
export const tabPanelBottomPadding = 16;
export const chatWindowMaxWidth = 960;
export const detailsBarMaxWidth = 548;

const commonBorderRadius = 20;

export const getDesignTokens = (
  mode: PaletteMode,
  currentBreakpoints = customBreakpoints,
  currentPalette: Palette = customPalette(mode),
  currentTypography: TypographyOptions = customTypographyOptions(mode)
): ThemeOptions => {
  // TODO: I am a one off that cannot use the core global
  // this check cannot be in the global scope, otherwise tests break
  const LAYOUT_TOGGLE_STORAGE_KEY = "ENABLE_ALTERNATE_LAYOUT";
  const useAlternateLayout = localStorage.getItem(LAYOUT_TOGGLE_STORAGE_KEY) === "true";
  currentPalette = useAlternateLayout ? themeStandard : customPalette(mode);
  return {
    palette: currentPalette,
    breakpoints: currentBreakpoints,
    typography: useAlternateLayout ? standardTypographyOptions(mode) : currentTypography,
    components: {
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: currentPalette.secondary.main,
            color: currentPalette.header.main,
            fontSize: pxToRem(14),
            height: pxToRem(34),
            width: pxToRem(34),
            marginRight: 0,
            [currentBreakpoints.up(breakpointValues.md)]: {
              marginRight: pxToRem(12),
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            position: "static",
            height: "4rem",
            lineHeight: "4rem",
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            "&& .Mui-selected, && .Mui-selected:hover": {
              "&, & .MuiIcon-root": {
                color: currentPalette.text.primary,
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: currentPalette.grey["300"],
              fontWeight: "bold",
            },
            color: currentPalette.text.secondary,
            backgroundColor: currentPalette.background.default,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            marginLeft: "auto",
            marginRight: 0,
            color: currentPalette.text.secondary,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            "&& .Mui-selected": {
              "& .MuiListItemText-primary": {
                fontWeight: "bold",
              },
            },
            "&:hover": {
              "& .MuiListItemText-primary": {
                fontWeight: "bold",
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: pxToRem(15),
            color: currentPalette.text.primary,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            marginLeft: "2px",
            marginRight: "2px",
            "&.Mui-disabled": {
              backgroundColor: currentPalette.grey["200"],
              color: currentPalette.text.disabled,
            },
            "&:focus": {
              outlineStyle: "solid",
              outlineWidth: "2px",
              outlineColor: currentPalette.primary.main,
              backgroundColor: currentPalette.grey["300"],
              boxShadow: "none",
              color: useAlternateLayout ? currentPalette.text.primary : undefined,
            },
            "&:hover": {
              backgroundColor: currentPalette.grey["200"],
              boxShadow: "none",
              color: useAlternateLayout ? currentPalette.text.primary : undefined,
            },
            borderRadius: useAlternateLayout ? pxToRem(100) : pxToRem(2),
            boxShadow: "none",
            fontSize: pxToRem(13),
            lineHeight: pxToRem(18),
            fontWeight: "700",
            letterSpacing: pxToRem(1),
            height: useAlternateLayout ? pxToRem(40) : pxToRem(36),
            [currentBreakpoints.down(breakpointValues.md)]: {
              height: pxToRem(44),
            },
            backgroundColor: useAlternateLayout
              ? currentPalette.primary.main
              : currentPalette.background.paper,
            color: useAlternateLayout
              ? currentPalette.primary.contrastText
              : currentPalette.primary.main,
          },
          containedPrimary: {
            backgroundColor: currentPalette.text.primary,
            color: currentPalette.background.default,
            "&:focus": {
              color: currentPalette.background.default,
              backgroundColor: currentPalette.grey["500"],
              outlineStyle: "solid",
              outlineWidth: "1px",
              outlineColor: currentPalette.divider,
              boxShadow: "none",
            },
            "&:hover": {
              color: currentPalette.background.default,
              backgroundColor: currentPalette.grey["900"],
              boxShadow: "none",
            },
          },
          containedError: {
            backgroundColor: currentPalette.error.main,
            color: currentPalette.primary.contrastText,
            borderColor: currentPalette.text.primary,
            "&:focus": {
              color: currentPalette.primary.contrastText,
              backgroundColor: currentPalette.error.dark,
              outlineStyle: "solid",
              outlineWidth: "1px",
              boxShadow: "none",
            },
            "&:hover": {
              color: currentPalette.primary.contrastText,
              backgroundColor: currentPalette.error.light,
              boxShadow: "none",
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&:focus": {
              outlineStyle: "solid",
              outlineWidth: "2px",
              "&.MuiPickersCalendarHeader-switchViewButton": {
                margin: 2,
                padding: 1,
              },
            },
            color: currentPalette.primary.main,
          },
        },
      },
      PageHeaderCalloutDiv: {
        styleOverrides: {
          root: {
            border: "8px solid transparent",
            borderBottomColor: currentPalette.background.default,
            height: 0,
            position: "fixed",
            top: useAlternateLayout
              ? `${appHeaderHeight - 15}px`
              : `${appHeaderHeight + pageHeaderHeight - 15}px`,
            width: 0,
          },
        },
      },
      ChatBubbleDiv: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? currentPalette.background.default : "none",
            border: "1px solid",
            borderColor: currentPalette.divider,
            borderRadius: `${commonBorderRadius}px`,
            color: currentPalette.text.primary,
            fontSize: pxToRem(13),
            lineHeight: pxToRem(20),
            padding: `${pxToRem(7)} ${pxToRem(20)}`,
            margin: `auto auto ${pxToRem(4)} 0`,
            maxWidth: "100%",
            width: "fit-content",
            [currentBreakpoints.up(breakpointValues.lg)]: {
              maxWidth: "100%",
              fontSize: pxToRem(15),
              lineHeight: pxToRem(23),
              marginBottom: pxToRem(8),
              padding: `${pxToRem(9)} ${pxToRem(20)} ${pxToRem(8)} ${pxToRem(20)}`,
            },
            [currentBreakpoints.up(breakpointValues.xs)]: {
              maxWidth: "100%",
              fontSize: pxToRem(15),
              lineHeight: pxToRem(23),
              marginBottom: pxToRem(8),
              padding: `${pxToRem(9)} ${pxToRem(20)} ${pxToRem(8)} ${pxToRem(20)}`,
              width: "fit-content",
            },
          },
        },
        variants: [
          {
            props: { variant: "info" },
            style: {
              borderColor: currentPalette.info.main,
            },
          },
          {
            props: { variant: "success" },
            style: {
              borderColor: currentPalette.success.main,
            },
          },
          {
            props: { variant: "warn" },
            style: {
              borderColor: currentPalette.text.primary,
            },
          },
          {
            props: { variant: "critical" },
            style: {
              borderColor: currentPalette.error.main,
            },
          },
          {
            props: { variant: "outbound" },
            style: {
              backgroundColor: currentPalette.primary.dark,
              borderColor: currentPalette.primary.contrastText,
              color: currentPalette.header.main,
              marginLeft: "auto",
              marginRight: 0,
            },
          },
        ],
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: "2px",
              borderColor: currentPalette.primary.light,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            "& .Mui-selected:hover, & .Mui-selected:focus": {
              color: currentPalette.text.primary,
              fontWeight: "700",
            },
            backgroundColor: useAlternateLayout
              ? currentPalette.background.paper
              : currentPalette.background.default,
            color: currentPalette.text.primary,
            width: "100%",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            width: "33.3%",
            boxSizing: "border-box",
            "&.Mui-focusVisible": {
              borderStyle: "solid",
              borderWidth: "2px",
            },
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          TabIndicatorProps: { style: { backgroundColor: currentPalette.primary.main } },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButtonGroup: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              backgroundColor: currentPalette.grey["300"],
              outlineStyle: "solid",
              outlineWidth: "2px",
              margin: 5,
              padding: 5,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            width: "auto",
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            width: "auto",
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              backgroundColor: currentPalette.grey["300"],
              outlineStyle: "solid",
              outlineWidth: "2px",
              margin: 5,
              padding: 5,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .Mui-focusVisible": {
              outlineColor: currentPalette.primary.main,
              outlineStyle: "solid",
              outlineWidth: "2px",
              outlineOffset: -2,
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: currentPalette.primary.main,
          },
          arrow: {
            color: currentPalette.primary.main,
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            "&& .Mui-focusVisible, && .MuiTablePagination-select": {
              "&:focus": {
                outlineStyle: "solid",
                outlineWidth: "2px",
              },
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            "&.MuiPickersDay-root.Mui-selected": {
              backgroundColor: currentPalette.primary.main,
              color: currentPalette.primary.contrastText,
              ":hover": {
                backgroundColor: currentPalette.primary.main,
                color: currentPalette.primary.contrastText,
              },
            },
            ":hover": {
              backgroundColor: currentPalette.primary.main,
              color: currentPalette.primary.contrastText,
            },
            ":focus": {
              outlineColor: currentPalette.primary.main,
              outlineStyle: "solid",
              outlineWidth: "2px",
            },
          },
        },
      },
      PrivatePickersMonth: {
        styleOverrides: {
          root: {
            "&.PrivatePickersMonth-root.Mui-selected": {
              backgroundColor: currentPalette.primary.main,
              color: currentPalette.primary.contrastText,
              ":hover": {
                backgroundColor: currentPalette.primary.main,
                color: currentPalette.primary.contrastText,
              },
            },
            ":hover": {
              backgroundColor: currentPalette.primary.main,
              color: currentPalette.primary.contrastText,
            },
            ":focus": {
              outlineColor: currentPalette.primary.main,
              outlineStyle: "solid",
              outlineWidth: "2px",
            },
          },
        },
      },
      PrivatePickersYear: {
        styleOverrides: {
          button: {
            "&&.PrivatePickersYear-yearButton.Mui-selected": {
              backgroundColor: currentPalette.primary.main,
              color: currentPalette.primary.contrastText,
              ":hover": {
                backgroundColor: currentPalette.primary.main,
                color: currentPalette.primary.contrastText,
              },
            },
          },
          root: {
            "& .PrivatePickersYear-yearButton": {
              ":hover": {
                backgroundColor: currentPalette.primary.main,
                color: currentPalette.primary.contrastText,
              },
              ":focus": {
                outlineColor: currentPalette.primary.main,
                outlineStyle: "solid",
                outlineWidth: "2px",
              },
            },
          },
        },
      },
    },
  };
};
